import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import type { BrageSvenHendelse } from '@/state/GameState';
import { KATEGORI_NAVN } from '@/systems/brage';
import { animateModalGlide } from '@/ui/modalGlide';
import { lagRundetRekt } from '@/ui/RoundedRect';
import { Kolonne } from '@/ui/layout';
import { lottieOverlay, type LottieHandle } from '@/ui/LottieOverlay';
import { bounceIn } from '@/ui/MikroAnim';
import { sfx } from '@/systems/sfx';
import { autoSpeed } from '@/systems/autoSpeed';

const SHEET_WIDTH = 920;
const SHEET_HEIGHT = 920;

export class SvenBrageVinnerModal {
  private layer?: Phaser.GameObjects.Container;
  private lottieHandle?: LottieHandle | null;

  constructor(private scene: Phaser.Scene) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  open(hendelse: BrageSvenHendelse): void {
    this.close();
    autoSpeed.paus();
    sfx.play('fanfare');
    this.layer = this.scene.add.container(0, 0).setDepth(220);
    animateModalGlide(this.scene, this.layer, { spillLyd: false });

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85)
      .setOrigin(0, 0)
      .setInteractive();
    backdrop.on('pointerup', () => this.close());
    this.layer.add(backdrop);

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = (GAME_HEIGHT - SHEET_HEIGHT) / 2;

    const sheet = lagRundetRekt(
      this.scene,
      sheetX,
      sheetY,
      SHEET_WIDTH,
      SHEET_HEIGHT,
      COLORS.wine,
      1,
      14,
    ).setStrokeStyle(6, 0xb8860b, 1);
    this.layer.add(sheet);

    const innerPad = 24;
    const inner = lagRundetRekt(
      this.scene,
      sheetX + innerPad,
      sheetY + innerPad,
      SHEET_WIDTH - innerPad * 2,
      SHEET_HEIGHT - innerPad * 2,
      0x4a1620,
      1,
      10,
    ).setStrokeStyle(2, 0xb8860b, 0.7);
    this.layer.add(inner);

    const krone = this.scene.add
      .text(sheetX + SHEET_WIDTH / 2, sheetY + 80, 'Brage', {
        fontFamily: FONTS.display,
        fontSize: fontStr(72),
        color: '#b8860b',
        fontStyle: '700',
      })
      .setOrigin(0.5, 0);
    this.layer.add(krone);
    bounceIn(krone, { toppScale: 1.2, varighet: 500 });

    // Lottie-trofé spinner inn over kronen
    this.lottieHandle = lottieOverlay.spill(
      this.scene,
      sheetX + SHEET_WIDTH / 2,
      sheetY + 150,
      'brage-trophy',
      {
        width: 320,
        height: 320,
        zIndex: 225,
      },
    );

    // Layout-primitiv (2026-06-04): den vertikale tekstsekvensen (navn →
    // «vinner Brage» → kategori → boktittel → vei-note) stables fra målt
    // høyde via Kolonne. Tekstene er sentrert (origin 0.5,0), så x holdes
    // i midten mens Kolonne kun setter topp-y. Et langt spiller-navn kan
    // wrappe til 2 linjer — før dette overlappet det «vinner Brage».
    const midtX = sheetX + SHEET_WIDTH / 2;
    const tekstKol = new Kolonne(midtX, sheetY + 230);

    const tittel = this.scene.add
      .text(midtX, 0, gameState.spillerFulltNavn, {
        fontFamily: FONTS.display,
        fontSize: fontStr(64),
        color: '#f4ead5',
        align: 'center',
        wordWrap: { width: SHEET_WIDTH - 96 },
      })
      .setOrigin(0.5, 0);
    tekstKol.legg(tittel, 0);
    this.layer.add(tittel);

    const undertittel = this.scene.add
      .text(midtX, 0, 'vinner Brage', {
        fontFamily: FONTS.display,
        fontSize: fontStr(48),
        color: '#b8860b',
        align: 'center',
      })
      .setOrigin(0.5, 0);
    tekstKol.legg(undertittel, 24);
    this.layer.add(undertittel);

    const kategori = this.scene.add
      .text(
        midtX,
        0,
        `i kategorien ${KATEGORI_NAVN[hendelse.kategori]} ${hendelse.aar}`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#f4d58d',
          fontStyle: 'italic',
          align: 'center',
          wordWrap: { width: SHEET_WIDTH - 96 },
        },
      )
      .setOrigin(0.5, 0);
    tekstKol.legg(kategori, 32);
    this.layer.add(kategori);

    const bokTittel = this.scene.add
      .text(midtX, 0, `«${hendelse.tittel}»`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(36),
        color: '#f4ead5',
        fontStyle: 'italic',
        align: 'center',
        wordWrap: { width: SHEET_WIDTH - 96 },
      })
      .setOrigin(0.5, 0);
    tekstKol.legg(bokTittel, 28);
    this.layer.add(bokTittel);

    if (hendelse.publiseringsvei) {
      const veiTekst = veiOmtale(hendelse.publiseringsvei);
      const note = this.scene.add
        .text(midtX, 0, veiTekst, {
          fontFamily: FONTS.body,
          fontSize: fontStr(16),
          color: '#d9a14b',
          fontStyle: 'italic',
          align: 'center',
          wordWrap: { width: SHEET_WIDTH - 96 },
        })
        .setOrigin(0.5, 0);
      tekstKol.legg(note, 28);
      this.layer.add(note);
    }

    const knappW = 280;
    const knappH = 72;
    const knappX = sheetX + (SHEET_WIDTH - knappW) / 2;
    const knappY = sheetY + SHEET_HEIGHT - 110;
    const knappBg = lagRundetRekt(
      this.scene,
      knappX,
      knappY,
      knappW,
      knappH,
      0xb8860b,
      1,
      8,
    ).setStrokeStyle(2, COLORS.wine, 0.7);
    const knappTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, 'Ta imot prisen', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add([knappBg, knappTxt]);
    knappBg.setInteractive({ useHandCursor: true });
    knappBg.on('pointerover', () => knappBg.setAlpha(0.9));
    knappBg.on('pointerout', () => knappBg.setAlpha(1));
    knappBg.on('pointerup', () => this.close());
  }

  close(): void {
    this.layer?.destroy(true);
    this.layer = undefined;
    this.lottieHandle?.destroy();
    this.lottieHandle = undefined;
    autoSpeed.fortsett();
  }
}

function veiOmtale(
  vei:
    | 'eget-forlag'
    | 'kapelle'
    | 'asketank'
    | 'gullsand'
    | 'calidris',
): string {
  if (vei === 'eget-forlag') {
    return 'Til tross for at du publiserte i ditt eget forlag — juryen overbeviste seg om manuset.';
  }
  if (vei === 'calidris') {
    return 'Selv Calidris-stempelet kunne ikke skjule kvaliteten.';
  }
  if (vei === 'asketank') return 'Asketank tar en velfortjent runde i rampelyset.';
  if (vei === 'kapelle') return 'Kapelle Damm har stadig en vinner i stallen.';
  return 'Gullsand-bok i toppen.';
}
